import { apiCall } from 'modules/core/services/api'
import { EngagementPostDto, SocialInsightsService } from 'openapi'
import { create } from 'zustand'

/**
 * Live engagement for recently published Instagram/Facebook posts, keyed by
 * normalized permalink. LIVE-FETCH ONLY (no persistence): fetched on calendar
 * open so deletes on the platform self-correct (a deleted post simply stops
 * being returned). Gated behind `analytics_section`. Covers each platform's
 * most-recent ~25 posts (no historical backfill).
 */
export const normalizePermalink = (url?: string): string =>
  url ? url.replace(/\?.*$/, '').replace(/\/+$/, '').toLowerCase() : ''

interface EngagementState {
  byPermalink: Record<string, EngagementPostDto>
  loading: boolean
  /** Re-fetches every call so the calendar reflects deletions/new posts on each
   * open; the `loading` guard only dedupes concurrent in-flight calls. */
  load: () => Promise<void>
  getByPermalink: (permalink?: string) => EngagementPostDto | undefined
}

export const useEngagementStore = create<EngagementState>((set, get) => ({
  byPermalink: {},
  loading: false,
  load: async () => {
    if (get().loading) return
    set({ loading: true })
    try {
      const res = await apiCall(SocialInsightsService.socialInsightsControllerGetEngagementV1, null)
      const map: Record<string, EngagementPostDto> = {}
      for (const post of res.posts) {
        if (post.permalink) map[normalizePermalink(post.permalink)] = post
      }
      set({ byPermalink: map, loading: false })
    } catch (err) {
      // Optional feed — degrade to whatever's loaded. `apiCall(..., null)`
      // re-throws, so surface unexpected failures (→ console bridge) but
      // stay quiet on the expected 403 when the gate is off for this account.
      const status = (err as { status?: number })?.status
      if (status !== 403) console.error('Failed to load engagement feed:', err)
      set({ loading: false })
    }
  },
  getByPermalink: permalink => {
    if (!permalink) return undefined
    return get().byPermalink[normalizePermalink(permalink)]
  },
}))
